/** A closed disclosure for the long sentences on the system and disclosure screens (ledger, security
 * history, assurance, prior disclosures, risks, transition).
 *
 * **Honesty copy is ranked, not deleted.** The sentence a reader must have beside a figure stays on
 * the page; the longer argument for it sits behind one closed control with every word kept. A
 * native `<details>` so it works without script and is reachable by keyboard, and so a screen test
 * can open it with one click.
 *
 * **Never put a sentence a gate reads by `innerText` in here.** A closed `<details>`'s content is
 * not rendered, so `innerText` returns nothing for it and a check on that sentence would pass or
 * fail for a reason that has nothing to do with the product. Those sentences stay visible.
 */

import { CaretRight } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { LazyDetails } from "../components/ui/LazyDetails";
import styles from "./SystemDisclosure.module.css";

export function SystemDisclosure({
  summary,
  children,
  testId,
  open,
  lazy = false,
}: {
  summary: string;
  children: ReactNode;
  testId?: string;
  open?: boolean;
  /** The body is not in the document while the disclosure is closed (`LazyDetails`), so the
   * text-budget guard counts none of it. Opt in per screen: a gate that reads the body by
   * `textContent` of a closed disclosure needs it rendered. */
  lazy?: boolean;
}) {
  if (lazy) {
    return (
      <LazyDetails
        className={styles.disclosure}
        testId={testId}
        open={open}
        summary={
          <>
            <CaretRight size={16} weight="regular" aria-hidden="true" />
            {summary}
          </>
        }
      >
        <div className={styles.body}>{children}</div>
      </LazyDetails>
    );
  }
  return (
    <details className={styles.disclosure} data-testid={testId} open={open}>
      <summary>
        <CaretRight size={16} weight="regular" aria-hidden="true" />
        {summary}
      </summary>
      <div className={styles.body}>{children}</div>
    </details>
  );
}
